<template>
  <component
    :is="component"
    ref="wrapperRef"
    :class="ns.b()"
    v-bind="$attrs"
    :style="styles"
  >
    <slot />
    <ResizeTrigger
      v-for="direction in allowDirections"
      :key="direction"
      :prefix-cls="ns.b('trigger')"
      :class="ns.b(`direction-${direction}`)"
      :direction="isHorizontal(direction) ? 'horizontal' : 'vertical'"
      :direction-name="direction"
      @mousedown="(e:any) =>onMoveStart(direction, e)
      "
      @resize="(entry) => onTiggerResize(direction, entry)"
    >
      <slot
        v-if="$slots['resize-trigger']"
        name="resize-trigger"
        :direction="direction"
      />
      <template v-if="$slots['resize-trigger-icon']" #icon>
        <slot name="resize-trigger-icon" :direction="direction" />
      </template>
    </ResizeTrigger>
  </component>
</template>
<script lang="ts" setup>
import { computed, reactive, ref, toRefs } from 'vue'
import { useNamespace } from '@lit-element/hooks'
import { isNumber } from '@lit-element/utils'
import { off, on } from './dom'
import ResizeTrigger from './resize-trigger.vue'
import {
  DIRECTION_BOTTOM,
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
  DIRECTION_TOP,
  allDirections,
  resizeBoxProps,
  useMergeState,
} from './data'
import type { DirectionType, PaddingCSSProperties } from './data'

defineOptions({ name: 'LtResizeBox' })
const props = defineProps(resizeBoxProps)
const emits = defineEmits([
  'update:width',
  'update:height',
  'movingStart',
  'moving',
  'movingEnd',
])

function getRealSize(pageSize: number, padding: number) {
  if (pageSize === 0) return 0
  const res = pageSize - padding
  return res <= 0 ? 0 : res
}

function isHorizontal(direction: DirectionType) {
  return [DIRECTION_TOP, DIRECTION_BOTTOM].includes(direction)
}

const { height: propHeight, width: propWidth, directions } = toRefs(props)

const [width, setWidth] = useMergeState<number | null>(
  null,
  reactive({
    value: propWidth,
  })
)

const [height, setHeight] = useMergeState<number | null>(
  null,
  reactive({
    value: propHeight,
  })
)

const wrapperRef = ref<HTMLDivElement>()

const paddingStyles = reactive<Partial<Record<PaddingCSSProperties, string>>>(
  {}
)

const ns = useNamespace('resizebox')
const styles = computed(() => {
  const obj = {
    ...(isNumber(width.value) ? { width: `${width.value}px` } : {}),
    ...(isNumber(height.value) ? { height: `${height.value}px` } : {}),
    ...paddingStyles,
  }
  return obj
})
const allowDirections = computed(() =>
  directions.value.filter((direction) => allDirections.includes(direction))
)

const record = {
  direction: '',
  startPageX: 0,
  startPageY: 0,
  startWidth: 0,
  startHeight: 0,
  moving: false,
  padding: {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
}

function onMoving(e: MouseEvent) {
  if (!record.moving) return

  const { startPageX, startPageY, startWidth, startHeight, direction } = record
  let newWidth = startWidth
  let newHeight = startHeight

  // 往右移动的距离
  const offsetX = e.pageX - startPageX
  // 往下移动的距离
  const offsetY = e.pageY - startPageY

  switch (direction) {
    case DIRECTION_LEFT:
      newWidth = startWidth - offsetX
      setWidth(newWidth)
      emits('update:width', newWidth)
      break
    case DIRECTION_RIGHT:
      newWidth = startWidth + offsetX
      setWidth(newWidth)
      emits('update:width', newWidth)
      break
    case DIRECTION_TOP:
      newHeight = startHeight - offsetY
      setHeight(newHeight)
      emits('update:height', newHeight)
      break
    case DIRECTION_BOTTOM:
      newHeight = startHeight + offsetY
      setHeight(newHeight)
      emits('update:height', newHeight)
      break
    default:
      break
  }

  emits(
    'moving',
    {
      width: newWidth,
      height: newHeight,
    },
    e
  )
}

function onMoveEnd(e: MouseEvent) {
  record.moving = false

  off(window, 'mousemove', onMoving)
  off(window, 'mouseup', onMoveEnd)
  off(window, 'contextmenu', onMoveEnd)

  document.body.style.cursor = 'default'

  emits('movingEnd', e)
}

function onMoveStart(direction: DirectionType, e: MouseEvent) {
  emits('movingStart', e)

  record.moving = true
  record.startPageX = e.pageX
  record.startPageY = e.pageY
  record.direction = direction

  const { top, left, right, bottom } = record.padding
  record.startWidth = getRealSize(
    wrapperRef.value?.clientWidth || 0,
    left + right
  )
  record.startHeight = getRealSize(
    wrapperRef.value?.clientHeight || 0,
    top + bottom
  )

  on(window, 'mousemove', onMoving)
  on(window, 'mouseup', onMoveEnd)
  on(window, 'contextmenu', onMoveEnd)

  document.body.style.cursor = isHorizontal(direction)
    ? 'row-resize'
    : 'col-resize'
}

function onTiggerResize(direction: DirectionType, entry: ResizeObserverEntry) {
  const { width, height } = entry.contentRect
  const size = isHorizontal(direction) ? height : width
  record.padding[direction] = size
  paddingStyles[`padding-${direction}` as PaddingCSSProperties] = `${size}px`
}
</script>
